import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";
import { buildOutput } from "../lib/content";
import { getWallet, shortFingerprint } from "../lib/wallet";
import { saveDraft } from "../lib/api";
import { ReceiptPreview } from "../components/ReceiptPreview";
import { LiveKatlasReceipt } from "../components/LiveKatlasReceipt";
import { lookupCiergeBySectorId } from "../lib/ciergeRegistry";
import { toast } from "sonner";

const STORAGE_KEY = "cierge.draft.in-progress";

export const Output = () => {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [savedDraft, setSavedDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showReceiptPreview, setShowReceiptPreview] = useState(false);
  const [liveKatlasResult, setLiveKatlasResult] = useState(null);

  const selection = useMemo(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    setWallet(getWallet());
  }, []);

  useEffect(() => {
    if (!selection || !selection.userTypeId || !selection.sectorId || !selection.problemId || !selection.scopeId) {
      navigate("/explore");
    }
  }, [selection, navigate]);

  if (!selection || !selection.userTypeId) return null;

  const output = buildOutput(selection);
  const cierge = lookupCiergeBySectorId(output.selection.sector.id);
  const showCiergeLink =
    cierge && cierge.status === "live" && cierge.external_url;

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const payload = {
        wallet_fingerprint: wallet ? wallet.fingerprint : null,
        wallet_handle: wallet ? wallet.handle : null,
        user_type: output.selection.user_type,
        sector: output.selection.sector,
        problem: output.selection.problem,
        scope: output.selection.scope,
        headline: output.headline,
      };
      const result = await saveDraft(payload);
      setSavedDraft(result);
      toast.success(
        wallet
          ? "Draft saved and bound to your wallet."
          : "Draft saved locally. Create a wallet to keep it across sessions.",
      );
    } catch (e) {
      toast.error("Could not save draft. Backend unavailable.");
    } finally {
      setSaving(false);
    }
  };

  const handleSandboxRoute = () => {
    const params = new URLSearchParams({
      intent: output.next_step.id,
      sector: output.selection.sector.id,
      problem: output.selection.problem.id,
    });
    if (savedDraft) params.set("draft", savedDraft.id);
    navigate(`/sandbox-request?${params.toString()}`);
  };

  return (
    <div data-testid="output-page" className="bg-white">
      <section className="border-b border-border bg-[#FAFAFA]">
        <div className="mx-auto max-w-[1280px] px-6 py-14 lg:px-10">
          <p
            data-testid="output-summary-chip"
            className="inline-flex items-center gap-3 border border-border bg-white px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-foreground"
          >
            <span className="h-1.5 w-1.5 bg-emerald-600" /> {output.summary_chip}
          </p>
          <h1
            data-testid="output-headline"
            className="mt-6 max-w-4xl font-serif text-4xl font-light leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl"
          >
            {output.headline}.
          </h1>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto grid max-w-[1280px] gap-12 px-6 py-16 md:grid-cols-12 md:py-20 lg:px-10">
          <div className="md:col-span-7">
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
              Tailored explainer
            </p>
            <div className="mt-6 space-y-5 text-base leading-relaxed text-foreground/90">
              {output.explainer.map((p, i) => (
                <p key={i} data-testid={`output-explainer-${i}`}>
                  {p}
                </p>
              ))}
            </div>

            <Separator className="my-10" />

            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
              Recommended case study · {output.selection.sector.label}
            </p>
            <article
              data-testid="output-case-study"
              className="mt-6 border border-border bg-white p-8"
            >
              <h3 className="font-serif text-2xl font-light tracking-tight">
                {output.case_study.title}
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                {output.case_study.body}
              </p>
              {showCiergeLink && (
                <a
                  href={cierge.external_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`cierge-link-${cierge.id}`}
                  className="mt-6 inline-flex items-center gap-2 border-b border-foreground/40 pb-1 font-mono text-[11px] uppercase tracking-[0.18em] text-foreground transition-colors hover:border-foreground"
                >
                  Visit {cierge.label} →
                </a>
              )}
              <p className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-emerald-800">
                <span className="h-1.5 w-1.5 bg-emerald-600" />
                Working case-study summary · synthetic, not a customer reference
              </p>
            </article>
          </div>

          <aside className="md:col-span-5">
            <div className="sticky top-24 space-y-6">
              <div
                data-testid="output-next-step"
                className="border border-foreground bg-white p-8"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-emerald-800">
                  Recommended next step
                </p>
                <h3 className="mt-4 font-serif text-3xl font-light tracking-tight">
                  {output.next_step.label}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                  {output.next_step.summary}
                </p>
                <Button
                  type="button"
                  data-testid="output-next-cta"
                  onClick={handleSandboxRoute}
                  className="mt-6 w-full rounded-none font-mono text-xs uppercase tracking-[0.18em]"
                >
                  {output.next_step.cta} →
                </Button>
              </div>

              <div
                data-testid="output-save-block"
                className="border border-border bg-[#FAFAFA] p-8"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
                  Save this draft
                </p>
                <h4 className="mt-3 font-serif text-xl font-light tracking-tight">
                  Keep custody of your shaping work.
                </h4>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {wallet
                    ? "Your wallet is active. The draft binds to your wallet fingerprint only."
                    : "No wallet present. You can still save the draft, but it will not be retrievable from another session unless you create a wallet."}
                </p>
                {wallet ? (
                  <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-foreground">
                    key {shortFingerprint(wallet.fingerprint)}
                  </p>
                ) : null}
                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <Button
                    type="button"
                    onClick={handleSaveDraft}
                    disabled={saving || !!savedDraft}
                    data-testid="output-save-cta"
                    variant="outline"
                    className="rounded-none border-foreground/40 bg-white font-mono text-xs uppercase tracking-[0.18em] hover:border-foreground"
                  >
                    {savedDraft ? "Draft saved ✓" : saving ? "Saving…" : "Save draft"}
                  </Button>
                  {!wallet && (
                    <Button
                      asChild
                      type="button"
                      data-testid="output-create-wallet-cta"
                      variant="ghost"
                      className="rounded-none font-mono text-xs uppercase tracking-[0.18em]"
                    >
                      <Link to="/wallet">Create wallet →</Link>
                    </Button>
                  )}
                </div>
              </div>

              <div className="border border-border bg-white p-6 font-mono text-[10px] uppercase leading-relaxed tracking-[0.16em] text-muted-foreground">
                <p>› Selective disclosure</p>
                <p className="mt-2">
                  Only your selections and your public-key fingerprint are
                  shared. No identifying data, no organisation context.
                </p>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* LIVE KATLAS + REFERENCE SHAPES */}
      <section
        data-testid="receipt-preview-section"
        className="border-b border-border bg-[#FAFAFA]"
      >
        <div className="mx-auto max-w-[1280px] px-6 py-16 md:py-20 lg:px-10">
          <div className="grid gap-10 md:grid-cols-12">
            <div className="md:col-span-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
                Receipt state
              </p>
              <h2 className="mt-4 font-serif text-3xl font-light leading-tight tracking-tight sm:text-4xl">
                Live KATLAS receipt — and the reference shapes.
              </h2>
              <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
                The panel on the right is the authoritative receipt state
                for this draft. It is "unsigned — pending live KATLAS key",
                "live signing failed", or a real signed KATLAS receipt —
                never collapsed into a single ambiguous state. The
                synthetic + illustrative reference shapes below show what
                v1 stores locally and what the KATLAS envelope looks like,
                but they are reference material only. If a live signed
                receipt is present, the illustrative panel is marked as
                reference and is not treated as the receipt state.
              </p>
              {!showReceiptPreview ? (
                <Button
                  type="button"
                  data-testid="receipt-preview-toggle"
                  onClick={() => setShowReceiptPreview(true)}
                  variant="outline"
                  className="mt-8 rounded-none border-foreground/40 bg-white font-mono text-xs uppercase tracking-[0.18em] hover:border-foreground"
                >
                  Reveal reference shapes →
                </Button>
              ) : (
                <p className="mt-8 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-emerald-800">
                  <span className="h-1.5 w-1.5 bg-emerald-600" />
                  Reference shapes · revealed
                </p>
              )}
            </div>
            <div className="md:col-span-8 space-y-6">
              <LiveKatlasReceipt
                savedDraft={savedDraft}
                output={output}
                wallet={wallet}
                onLiveResultChange={setLiveKatlasResult}
              />
              {showReceiptPreview ? (
                <ReceiptPreview
                  savedDraft={savedDraft}
                  output={output}
                  wallet={wallet}
                  liveReceiptPresent={!!liveKatlasResult}
                />
              ) : (
                <div
                  data-testid="receipt-preview-placeholder"
                  className="grid h-full min-h-[200px] place-items-center border border-dashed border-border bg-white p-10 text-center"
                >
                  <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                    Synthetic + illustrative reference shapes are hidden by default. Reveal to see both side by side.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto flex max-w-[1280px] flex-wrap items-center justify-between gap-4 px-6 py-8 lg:px-10">
          <Button
            asChild
            variant="ghost"
            data-testid="output-restart"
            className="rounded-none font-mono text-xs uppercase tracking-[0.18em]"
          >
            <Link to="/explore">← Reshape selections</Link>
          </Button>
          <Link
            to="/about"
            className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground"
          >
            What is KATLAS →
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Output;
